import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useItems } from "../../contexts/item_context";
import "./quicktype.css";

/* ────────────────────────────────────────────────────────────
   IndexedDB persistence (no external deps)
   ──────────────────────────────────────────────────────────── */
const IDB_DB = "quicktype_prefs";
const IDB_STORE = "kv";
const IDB_KEY = "preferences";
const IDB_VERSION = 1;

function openDB() {
    return new Promise((resolve, reject) => {
        if (typeof indexedDB === "undefined") {
            reject(new Error("IndexedDB not available"));
            return;
        }
        const req = indexedDB.open(IDB_DB, IDB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(IDB_STORE)) {
                db.createObjectStore(IDB_STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function loadPrefs() {
    try {
        const db = await openDB();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(IDB_STORE, "readonly");
            const store = tx.objectStore(IDB_STORE);
            const req = store.get(IDB_KEY);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    } catch {
        return null;
    }
}

async function savePrefs(value) {
    try {
        const db = await openDB();
        await new Promise((resolve, reject) => {
            const tx = db.transaction(IDB_STORE, "readwrite");
            const store = tx.objectStore(IDB_STORE);
            const req = store.put(value, IDB_KEY);
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
    } catch {
        /* ignore — storage failures shouldn't break the UI */
    }
}

/* ────────────────────────────────────────────────────────────
   LANGUAGES, STYLES, OPTIONS
   ──────────────────────────────────────────────────────────── */
const languages = [
    {
        id: "dart",
        label: "Dart",
        ext: "dart",
        styles: [
            { id: "plain", label: "Plain Class" },
            { id: "json_serializable", label: "json_serializable" },
            { id: "freezed", label: "Freezed" },
        ],
        options: [
            { id: "nullSafety", label: "Null safety", default: true, hint: "Make all fields nullable (T?)" },
            { id: "required", label: "Required fields", default: false, hint: "Mark fields as required in constructor" },
            { id: "defaults", label: "Default values", default: false, hint: "Provide default literal values" },
            { id: "copyWith", label: "copyWith()", default: true },
            { id: "equality", label: "== / hashCode", default: false },
            { id: "toStringM", label: "toString()", default: false },
            { id: "finalFields", label: "final fields", default: true },
        ],
    },
    {
        id: "typescript",
        label: "TypeScript",
        ext: "ts",
        styles: [
            { id: "interface", label: "Interface" },
            { id: "type", label: "Type Alias" },
            { id: "class", label: "Class" },
        ],
        options: [
            { id: "nullSafety", label: "Optional fields (?)", default: true },
            { id: "readonly", label: "readonly", default: false },
            { id: "exportType", label: "export", default: true },
        ],
    },
    {
        id: "python",
        label: "Python",
        ext: "py",
        styles: [
            { id: "dataclass", label: "Dataclass" },
            { id: "pydantic", label: "Pydantic" },
            { id: "typeddict", label: "TypedDict" },
        ],
        options: [
            { id: "nullSafety", label: "Optional[...]", default: true },
            { id: "defaults", label: "Default = None", default: true },
            { id: "frozen", label: "Frozen / immutable", default: false },
        ],
    },
    {
        id: "java",
        label: "Java",
        ext: "java",
        styles: [
            { id: "pojo", label: "POJO" },
            { id: "record", label: "Record" },
            { id: "lombok", label: "Lombok @Data" },
        ],
        options: [
            { id: "equality", label: "equals / hashCode", default: false },
            { id: "toStringM", label: "toString()", default: false },
            { id: "jacksonAnnotations", label: "Jackson @JsonProperty", default: false },
        ],
    },
    {
        id: "csharp",
        label: "C#",
        ext: "cs",
        styles: [
            { id: "class", label: "Class" },
            { id: "record", label: "Record" },
            { id: "newtonsoft", label: "Newtonsoft.Json" },
        ],
        options: [
            { id: "nullSafety", label: "Nullable (T?)", default: true },
            { id: "initOnly", label: "init-only props", default: false },
        ],
    },
    {
        id: "golang",
        label: "Go",
        ext: "go",
        styles: [
            { id: "struct", label: "Struct" },
            { id: "struct_omitempty", label: "Struct (omitempty)" },
        ],
        options: [
            { id: "pointers", label: "Pointer fields", default: false, hint: "Use *T for nullability" },
        ],
    },
];

/* ────────────────────────────────────────────────────────────
   HELPERS
   ──────────────────────────────────────────────────────────── */
const pascal = (s) =>
    s
        .replace(/[_\s-]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
        .replace(/^./, (c) => c.toUpperCase())
        .replace(/[^A-Za-z0-9]/g, "");

const camel = (s) => {
    const p = pascal(s);
    return p.charAt(0).toLowerCase() + p.slice(1);
};

const snake = (s) =>
    s
        .replace(/([a-z])([A-Z])/g, "$1_$2")
        .replace(/[\s-]+/g, "_")
        .toLowerCase();

const singularize = (s) => {
    if (/ies$/i.test(s)) return s.slice(0, -3) + "y";
    if (/sses$/i.test(s)) return s.slice(0, -2);
    if (/s$/i.test(s) && !/ss$/i.test(s)) return s.slice(0, -1);
    return s;
};

/* ────────────────────────────────────────────────────────────
   SCHEMA INFERENCE
   ──────────────────────────────────────────────────────────── */
function buildSchema(json) {
    const classes = new Map();
    const usedNames = new Set();

    const uniqueName = (base) => {
        let name = pascal(base) || "Model";
        if (!usedNames.has(name)) {
            usedNames.add(name);
            return name;
        }
        let i = 2;
        while (usedNames.has(name + i)) i++;
        usedNames.add(name + i);
        return name + i;
    };

    const mergeShapes = (a, b) => {
        const out = { ...a };
        for (const k of Object.keys(b)) {
            if (!(k in out)) {
                out[k] = { ...b[k], optional: true };
            } else {
                out[k] = mergeTypes(out[k], b[k]);
            }
        }
        for (const k of Object.keys(out)) {
            if (!(k in b)) out[k] = { ...out[k], optional: true };
        }
        return out;
    };

    const mergeTypes = (a, b) => {
        if (a.kind === "null") return { ...b, nullable: true };
        if (b.kind === "null") return { ...a, nullable: true };
        if (a.kind === b.kind) {
            if (a.kind === "object") {
                return {
                    kind: "object",
                    fields: mergeShapes(a.fields, b.fields),
                    className: a.className || b.className,
                };
            }
            if (a.kind === "array") {
                return { kind: "array", item: mergeTypes(a.item, b.item) };
            }
            return a;
        }
        if (
            (a.kind === "integer" && b.kind === "number") ||
            (a.kind === "number" && b.kind === "integer")
        ) {
            return { kind: "number" };
        }
        return { kind: "any" };
    };

    const inferType = (value, hint) => {
        if (value === null) return { kind: "null" };
        if (Array.isArray(value)) {
            if (value.length === 0) return { kind: "array", item: { kind: "any" } };
            let itemType = inferType(value[0], singularize(hint || "Item"));
            for (let i = 1; i < value.length; i++) {
                itemType = mergeTypes(itemType, inferType(value[i], singularize(hint || "Item")));
            }
            return { kind: "array", item: itemType };
        }
        if (typeof value === "object") {
            const fields = {};
            for (const [k, v] of Object.entries(value)) {
                fields[k] = inferType(v, k);
            }
            return { kind: "object", fields, className: hint };
        }
        if (typeof value === "string") return { kind: "string" };
        if (typeof value === "boolean") return { kind: "boolean" };
        if (typeof value === "number") {
            return Number.isInteger(value) ? { kind: "integer" } : { kind: "number" };
        }
        return { kind: "any" };
    };

    const registerClasses = (type) => {
        if (!type) return;
        if (type.kind === "object") {
            const name = uniqueName(type.className || "Model");
            type.className = name;
            const fields = [];
            for (const [k, ft] of Object.entries(type.fields)) {
                registerClasses(ft);
                if (ft.kind === "array") registerClasses(ft.item);
                fields.push({ key: k, type: ft });
            }
            classes.set(name, { name, fields });
        } else if (type.kind === "array") {
            registerClasses(type.item);
        }
    };

    const root = inferType(json, "Root");
    registerClasses(root);
    return { root, classes: Array.from(classes.values()) };
}

/* ────────────────────────────────────────────────────────────
   TYPE RENDERERS
   ──────────────────────────────────────────────────────────── */
const renderType = {
    dart: (t) => {
        switch (t.kind) {
            case "string": return "String";
            case "integer": return "int";
            case "number": return "double";
            case "boolean": return "bool";
            case "null":
            case "any": return "dynamic";
            case "array": return `List<${renderType.dart(t.item)}>`;
            case "object": return t.className || "dynamic";
            default: return "dynamic";
        }
    },
    typescript: (t) => {
        switch (t.kind) {
            case "string": return "string";
            case "integer":
            case "number": return "number";
            case "boolean": return "boolean";
            case "null": return "null";
            case "any": return "any";
            case "array": return `${renderType.typescript(t.item)}[]`;
            case "object": return t.className || "any";
            default: return "any";
        }
    },
    python: (t) => {
        switch (t.kind) {
            case "string": return "str";
            case "integer": return "int";
            case "number": return "float";
            case "boolean": return "bool";
            case "null":
            case "any": return "Any";
            case "array": return `List[${renderType.python(t.item)}]`;
            case "object": return t.className;
            default: return "Any";
        }
    },
    java: (t) => {
        switch (t.kind) {
            case "string": return "String";
            case "integer": return "Integer";
            case "number": return "Double";
            case "boolean": return "Boolean";
            case "null":
            case "any": return "Object";
            case "array": return `List<${renderType.java(t.item)}>`;
            case "object": return t.className || "Object";
            default: return "Object";
        }
    },
    csharp: (t) => {
        switch (t.kind) {
            case "string": return "string";
            case "integer": return "long";
            case "number": return "double";
            case "boolean": return "bool";
            case "null":
            case "any": return "object";
            case "array": return `List<${renderType.csharp(t.item)}>`;
            case "object": return t.className || "object";
            default: return "object";
        }
    },
    golang: (t) => {
        switch (t.kind) {
            case "string": return "string";
            case "integer": return "int64";
            case "number": return "float64";
            case "boolean": return "bool";
            case "null":
            case "any": return "interface{}";
            case "array": return `[]${renderType.golang(t.item)}`;
            case "object": return t.className;
            default: return "interface{}";
        }
    },
};

/* ────────────────────────────────────────────────────────────
   DEFAULT-VALUE LITERALS
   ──────────────────────────────────────────────────────────── */
const defaultLiteral = {
    dart: (t) => {
        switch (t.kind) {
            case "string": return "''";
            case "integer": return "0";
            case "number": return "0.0";
            case "boolean": return "false";
            case "array": return "const []";
            default: return null;
        }
    },
    python: (t) => {
        switch (t.kind) {
            case "string": return "''";
            case "integer": return "0";
            case "number": return "0.0";
            case "boolean": return "False";
            case "array": return "field(default_factory=list)";
            default: return null;
        }
    },
};

/* ────────────────────────────────────────────────────────────
   DART
   ──────────────────────────────────────────────────────────── */
function genDart(schema, style, opt) {
    const { classes } = schema;

    if (style === "freezed") {
        let out = `import 'package:freezed_annotation/freezed_annotation.dart';\n\n`;
        out += `part 'models.freezed.dart';\npart 'models.g.dart';\n\n`;
        for (const cls of classes) {
            out += `@freezed\nclass ${cls.name} with _$${cls.name} {\n`;
            out += `  const factory ${cls.name}({\n`;
            for (const f of cls.fields) {
                const t = renderType.dart(f.type);
                const req = opt.required ? "required " : "";
                const nullable = opt.nullSafety && !opt.required ? "?" : "";
                out += `    ${req}${t}${nullable} ${camel(f.key)},\n`;
            }
            out += `  }) = _${cls.name};\n\n`;
            out += `  factory ${cls.name}.fromJson(Map<String, dynamic> json) => _$${cls.name}FromJson(json);\n}\n\n`;
        }
        return out.trim();
    }

    if (style === "json_serializable") {
        let out = `import 'package:json_annotation/json_annotation.dart';\n\n`;
        out += `part 'models.g.dart';\n\n`;
        for (const cls of classes) {
            out += `@JsonSerializable()\nclass ${cls.name} {\n`;
            for (const f of cls.fields) {
                const t = renderType.dart(f.type);
                const finalKw = opt.finalFields ? "final " : "";
                const nullable = opt.nullSafety ? "?" : "";
                if (camel(f.key) !== f.key) out += `  @JsonKey(name: '${f.key}')\n`;
                out += `  ${finalKw}${t}${nullable} ${camel(f.key)};\n`;
            }
            out += `\n  ${cls.name}({\n`;
            for (const f of cls.fields) {
                const req = opt.required ? "required " : "";
                out += `    ${req}this.${camel(f.key)},\n`;
            }
            out += `  });\n\n`;
            out += `  factory ${cls.name}.fromJson(Map<String, dynamic> json) => _$${cls.name}FromJson(json);\n`;
            out += `  Map<String, dynamic> toJson() => _$${cls.name}ToJson(this);\n`;
            if (opt.copyWith) out += dartCopyWith(cls, opt);
            if (opt.equality) out += dartEquality(cls);
            if (opt.toStringM) out += dartToString(cls);
            out += `}\n\n`;
        }
        return out.trim();
    }

    // plain
    let out = "";
    for (const cls of classes) {
        out += `class ${cls.name} {\n`;
        for (const f of cls.fields) {
            const t = renderType.dart(f.type);
            const finalKw = opt.finalFields ? "final " : "";
            const nullable = opt.nullSafety ? "?" : "";
            out += `  ${finalKw}${t}${nullable} ${camel(f.key)};\n`;
        }
        out += `\n  ${cls.name}({\n`;
        for (const f of cls.fields) {
            const req = opt.required ? "required " : "";
            const lit = opt.defaults ? defaultLiteral.dart(f.type) : null;
            const def = lit ? ` = ${lit}` : "";
            out += `    ${req}this.${camel(f.key)}${def},\n`;
        }
        out += `  });\n\n`;

        out += `  factory ${cls.name}.fromJson(Map<String, dynamic> json) {\n`;
        out += `    return ${cls.name}(\n`;
        for (const f of cls.fields) {
            out += `      ${camel(f.key)}: ${dartFromJsonExpr(f.type, `json['${f.key}']`)},\n`;
        }
        out += `    );\n  }\n\n`;

        out += `  Map<String, dynamic> toJson() {\n    return {\n`;
        for (const f of cls.fields) {
            out += `      '${f.key}': ${dartToJsonExpr(f.type, camel(f.key))},\n`;
        }
        out += `    };\n  }\n`;

        if (opt.copyWith) out += dartCopyWith(cls, opt);
        if (opt.equality) out += dartEquality(cls);
        if (opt.toStringM) out += dartToString(cls);

        out += `}\n\n`;
    }
    return out.trim();
}

function dartCopyWith(cls, opt) {
    const nullable = opt.nullSafety ? "?" : "";
    let out = `\n  ${cls.name} copyWith({\n`;
    for (const f of cls.fields) {
        const t = renderType.dart(f.type);
        out += `    ${t}${nullable} ${camel(f.key)},\n`;
    }
    out += `  }) {\n    return ${cls.name}(\n`;
    for (const f of cls.fields) {
        const n = camel(f.key);
        out += `      ${n}: ${n} ?? this.${n},\n`;
    }
    out += `    );\n  }\n`;
    return out;
}

function dartEquality(cls) {
    let out = `\n  @override\n  bool operator ==(Object other) =>\n      identical(this, other) ||\n      other is ${cls.name} &&\n          runtimeType == other.runtimeType`;
    for (const f of cls.fields) {
        out += ` &&\n          ${camel(f.key)} == other.${camel(f.key)}`;
    }
    out += `;\n\n  @override\n  int get hashCode =>`;
    if (cls.fields.length === 0) {
        out += ` 0;\n`;
    } else {
        out += ` ${cls.fields.map((f) => `${camel(f.key)}.hashCode`).join(" ^ ")};\n`;
    }
    return out;
}

function dartToString(cls) {
    const parts = cls.fields.map((f) => `${camel(f.key)}: $${camel(f.key)}`).join(", ");
    return `\n  @override\n  String toString() => '${cls.name}(${parts})';\n`;
}

function dartFromJsonExpr(type, src) {
    if (type.kind === "object") return `${src} != null ? ${type.className}.fromJson(${src}) : null`;
    if (type.kind === "array") {
        if (type.item.kind === "object")
            return `(${src} as List?)?.map((e) => ${type.item.className}.fromJson(e)).toList()`;
        return `(${src} as List?)?.cast<${renderType.dart(type.item)}>()`;
    }
    if (type.kind === "integer") return `${src} as int?`;
    if (type.kind === "number") return `(${src} as num?)?.toDouble()`;
    if (type.kind === "string") return `${src} as String?`;
    if (type.kind === "boolean") return `${src} as bool?`;
    return src;
}

function dartToJsonExpr(type, name) {
    if (type.kind === "object") return `${name}?.toJson()`;
    if (type.kind === "array" && type.item.kind === "object")
        return `${name}?.map((e) => e.toJson()).toList()`;
    return name;
}

/* ────────────────────────────────────────────────────────────
   TYPESCRIPT
   ──────────────────────────────────────────────────────────── */
function genTypeScript(schema, style, opt) {
    const { classes } = schema;
    const exp = opt.exportType ? "export " : "";
    let out = "";

    if (style === "type") {
        for (const cls of classes) {
            out += `${exp}type ${cls.name} = {\n`;
            for (const f of cls.fields) {
                const opt1 = opt.nullSafety ? "?" : "";
                const ro = opt.readonly ? "readonly " : "";
                out += `  ${ro}${jsKey(f.key)}${opt1}: ${renderType.typescript(f.type)};\n`;
            }
            out += `};\n\n`;
        }
        return out.trim();
    }

    if (style === "class") {
        for (const cls of classes) {
            out += `${exp}class ${cls.name} {\n`;
            for (const f of cls.fields) {
                const ro = opt.readonly ? "readonly " : "";
                out += `  ${ro}${jsKey(f.key)}!: ${renderType.typescript(f.type)};\n`;
            }
            out += `\n  static fromJson(json: any): ${cls.name} {\n`;
            out += `    const obj = new ${cls.name}();\n`;
            for (const f of cls.fields) {
                out += `    obj.${jsKey(f.key)} = json['${f.key}'];\n`;
            }
            out += `    return obj;\n  }\n}\n\n`;
        }
        return out.trim();
    }

    for (const cls of classes) {
        out += `${exp}interface ${cls.name} {\n`;
        for (const f of cls.fields) {
            const opt1 = opt.nullSafety ? "?" : "";
            const ro = opt.readonly ? "readonly " : "";
            out += `  ${ro}${jsKey(f.key)}${opt1}: ${renderType.typescript(f.type)};\n`;
        }
        out += `}\n\n`;
    }
    return out.trim();
}

const jsKey = (k) => (/^[a-zA-Z_$][\w$]*$/.test(k) ? k : `'${k}'`);

/* ────────────────────────────────────────────────────────────
   PYTHON
   ──────────────────────────────────────────────────────────── */
function genPython(schema, style, opt) {
    const { classes } = schema;
    const ordered = [...classes].reverse();

    const wrap = (t) => (opt.nullSafety ? `Optional[${t}]` : t);

    if (style === "pydantic") {
        let out = `from typing import List, Optional, Any\nfrom pydantic import BaseModel, Field\n\n\n`;
        for (const cls of ordered) {
            out += `class ${cls.name}(BaseModel):\n`;
            if (opt.frozen) out += `    model_config = {"frozen": True}\n\n`;
            if (cls.fields.length === 0 && !opt.frozen) out += `    pass\n`;
            for (const f of cls.fields) {
                const pyType = wrap(pyTypeNoQuote(f.type));
                const fieldName = snake(f.key);
                if (fieldName !== f.key) {
                    out += `    ${fieldName}: ${pyType} = Field(${opt.defaults ? "None, " : ""}alias='${f.key}')\n`;
                } else {
                    out += `    ${fieldName}: ${pyType}${opt.defaults ? " = None" : ""}\n`;
                }
            }
            out += `\n\n`;
        }
        return out.trim();
    }

    if (style === "typeddict") {
        let out = `from typing import List, TypedDict, Optional, Any\n\n\n`;
        for (const cls of ordered) {
            const total = opt.nullSafety ? ", total=False" : "";
            out += `class ${cls.name}(TypedDict${total}):\n`;
            if (cls.fields.length === 0) out += `    pass\n`;
            for (const f of cls.fields) {
                out += `    ${snake(f.key)}: ${wrap(pyTypeNoQuote(f.type))}\n`;
            }
            out += `\n\n`;
        }
        return out.trim();
    }

    let out = `from dataclasses import dataclass, field\nfrom typing import List, Optional, Any\n\n\n`;
    for (const cls of ordered) {
        const dec = opt.frozen ? `@dataclass(frozen=True)` : `@dataclass`;
        out += `${dec}\nclass ${cls.name}:\n`;
        if (cls.fields.length === 0) out += `    pass\n`;
        for (const f of cls.fields) {
            const pyType = wrap(pyTypeNoQuote(f.type));
            let def = "";
            if (opt.defaults) {
                const lit = defaultLiteral.python(f.type);
                def = ` = ${opt.nullSafety ? "None" : (lit ?? "None")}`;
            }
            out += `    ${snake(f.key)}: ${pyType}${def}\n`;
        }
        out += `\n\n`;
    }
    return out.trim();
}

function pyTypeNoQuote(t) {
    switch (t.kind) {
        case "string": return "str";
        case "integer": return "int";
        case "number": return "float";
        case "boolean": return "bool";
        case "null":
        case "any": return "Any";
        case "array": return `List[${pyTypeNoQuote(t.item)}]`;
        case "object": return t.className;
        default: return "Any";
    }
}

/* ────────────────────────────────────────────────────────────
   JAVA
   ──────────────────────────────────────────────────────────── */
function genJava(schema, style, opt) {
    const { classes } = schema;
    let out = `import java.util.List;\nimport java.util.Objects;\n`;
    if (style === "lombok") out += `import lombok.Data;\n`;
    if (opt.jacksonAnnotations) out += `import com.fasterxml.jackson.annotation.JsonProperty;\n`;
    out += `\n`;

    for (const cls of classes) {
        if (style === "record") {
            out += `public record ${cls.name}(\n`;
            const lines = cls.fields.map((f) => {
                const ann = opt.jacksonAnnotations ? `    @JsonProperty("${f.key}") ` : "    ";
                return `${ann}${renderType.java(f.type)} ${camel(f.key)}`;
            });
            out += lines.join(",\n");
            out += `\n) {}\n\n`;
            continue;
        }

        if (style === "lombok") {
            out += `@Data\npublic class ${cls.name} {\n`;
            for (const f of cls.fields) {
                if (opt.jacksonAnnotations) out += `    @JsonProperty("${f.key}")\n`;
                out += `    private ${renderType.java(f.type)} ${camel(f.key)};\n`;
            }
            out += `}\n\n`;
            continue;
        }

        out += `public class ${cls.name} {\n`;
        for (const f of cls.fields) {
            if (opt.jacksonAnnotations) out += `    @JsonProperty("${f.key}")\n`;
            out += `    private ${renderType.java(f.type)} ${camel(f.key)};\n`;
        }
        out += `\n    public ${cls.name}() {}\n\n`;
        for (const f of cls.fields) {
            const t = renderType.java(f.type);
            const cap = pascal(f.key);
            out += `    public ${t} get${cap}() { return ${camel(f.key)}; }\n`;
            out += `    public void set${cap}(${t} ${camel(f.key)}) { this.${camel(f.key)} = ${camel(f.key)}; }\n`;
        }
        if (opt.equality) out += javaEquality(cls);
        if (opt.toStringM) out += javaToString(cls);
        out += `}\n\n`;
    }
    return out.trim();
}

function javaEquality(cls) {
    const fieldList = cls.fields.map((f) => camel(f.key)).join(", ");
    let out = `\n    @Override\n    public boolean equals(Object o) {\n`;
    out += `        if (this == o) return true;\n`;
    out += `        if (!(o instanceof ${cls.name})) return false;\n`;
    out += `        ${cls.name} that = (${cls.name}) o;\n        return `;
    if (cls.fields.length === 0) {
        out += `true;\n    }\n`;
    } else {
        out += cls.fields
            .map((f) => `Objects.equals(${camel(f.key)}, that.${camel(f.key)})`)
            .join(" && ");
        out += `;\n    }\n`;
    }
    out += `\n    @Override\n    public int hashCode() {\n        return Objects.hash(${fieldList});\n    }\n`;
    return out;
}

function javaToString(cls) {
    const parts = cls.fields.map((f) => `"${f.key}=" + ${camel(f.key)}`).join(' + ", " + ');
    return `\n    @Override\n    public String toString() {\n        return "${cls.name}{" + ${parts || '""'} + "}";\n    }\n`;
}

/* ────────────────────────────────────────────────────────────
   C#
   ──────────────────────────────────────────────────────────── */
function genCSharp(schema, style, opt) {
    const { classes } = schema;
    let out = `using System.Collections.Generic;\n`;
    if (style === "newtonsoft") out += `using Newtonsoft.Json;\n`;
    out += `\n`;

    const nullable = opt.nullSafety ? "?" : "";
    const setter = opt.initOnly ? "init" : "set";

    for (const cls of classes) {
        if (style === "record") {
            out += `public record ${cls.name}(\n`;
            const lines = cls.fields.map(
                (f) => `    ${renderType.csharp(f.type)}${nullable} ${pascal(f.key)}`
            );
            out += lines.join(",\n");
            out += `\n);\n\n`;
            continue;
        }

        out += `public class ${cls.name}\n{\n`;
        for (const f of cls.fields) {
            const t = renderType.csharp(f.type);
            const prop = pascal(f.key);
            if (style === "newtonsoft") out += `    [JsonProperty("${f.key}")]\n`;
            out += `    public ${t}${nullable} ${prop} { get; ${setter}; }\n`;
        }
        out += `}\n\n`;
    }
    return out.trim();
}

/* ────────────────────────────────────────────────────────────
   GO
   ──────────────────────────────────────────────────────────── */
function genGo(schema, style, opt) {
    const { classes } = schema;
    let out = `package main\n\n`;
    const omit = style === "struct_omitempty" ? ",omitempty" : "";

    for (const cls of classes) {
        out += `type ${cls.name} struct {\n`;
        for (const f of cls.fields) {
            let t = renderType.golang(f.type);
            if (opt.pointers && f.type.kind !== "array" && f.type.kind !== "object") {
                t = `*${t}`;
            }
            const fieldName = pascal(f.key);
            out += `\t${fieldName} ${t} \`json:"${f.key}${omit}"\`\n`;
        }
        out += `}\n\n`;
    }
    return out.trim();
}

/* ────────────────────────────────────────────────────────────
   ENTRY
   ──────────────────────────────────────────────────────────── */
function generateCode(jsonText, language, style, opt) {
    let parsed;
    try {
        parsed = JSON.parse(jsonText);
    } catch (e) {
        return `// Error parsing JSON: ${e.message}`;
    }
    if (parsed === null || typeof parsed !== "object") {
        return `// Root must be an object or array`;
    }

    const schema = Array.isArray(parsed)
        ? buildSchema({ items: parsed })
        : buildSchema(parsed);

    const generators = {
        dart: genDart,
        typescript: genTypeScript,
        python: genPython,
        java: genJava,
        csharp: genCSharp,
        golang: genGo,
    };
    const fn = generators[language] || genTypeScript;
    return fn(schema, style, opt);
}

/* ────────────────────────────────────────────────────────────
   COMPONENT
   ──────────────────────────────────────────────────────────── */
export default function Quicktype() {
    const { activeItem } = useItems();
    const [selectedLang, setSelectedLang] = useState("dart");
    const [selectedStyle, setSelectedStyle] = useState("plain");
    const [optionsByLang, setOptionsByLang] = useState(() => {
        const init = {};
        for (const lang of languages) {
            init[lang.id] = {};
            for (const o of lang.options) init[lang.id][o.id] = o.default;
        }
        return init;
    });
    const [output, setOutput] = useState("");
    const [copiedFlash, setCopiedFlash] = useState(false);
    const [optionsOpen, setOptionsOpen] = useState(false);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    const optionsRef = useRef(null);

    const jsonText = activeItem?.text || "";
    const currentLang = useMemo(
        () => languages.find((l) => l.id === selectedLang) || languages[0],
        [selectedLang]
    );
    const currentOptions = useMemo(
        () => optionsByLang[selectedLang] || {},
        [optionsByLang, selectedLang]
    );

    /* ── Load preferences from IndexedDB on first mount ── */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const saved = await loadPrefs();
            if (cancelled || !saved) {
                setPrefsLoaded(true);
                return;
            }

            // Validate that the saved language still exists
            if (saved.selectedLang && languages.some((l) => l.id === saved.selectedLang)) {
                setSelectedLang(saved.selectedLang);
            }

            // Validate the style after we know the language
            if (saved.selectedStyle) {
                const lang =
                    languages.find((l) => l.id === saved.selectedLang) || languages[0];
                if (lang.styles.some((s) => s.id === saved.selectedStyle)) {
                    setSelectedStyle(saved.selectedStyle);
                }
            }

            // Merge saved options on top of defaults so newly-added options still get their default
            if (saved.optionsByLang) {
                setOptionsByLang((prev) => {
                    const merged = { ...prev };
                    for (const lang of languages) {
                        merged[lang.id] = {
                            ...prev[lang.id],
                            ...(saved.optionsByLang[lang.id] || {}),
                        };
                    }
                    return merged;
                });
            }

            setPrefsLoaded(true);
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    /* ── Persist to IndexedDB on any change (after initial load) ── */
    useEffect(() => {
        if (!prefsLoaded) return;
        savePrefs({ selectedLang, selectedStyle, optionsByLang });
    }, [prefsLoaded, selectedLang, selectedStyle, optionsByLang]);

    /* ── Auto-correct invalid style when language changes ── */
    useEffect(() => {
        const exists = currentLang.styles.some((s) => s.id === selectedStyle);
        if (!exists) setSelectedStyle(currentLang.styles[0].id);
    }, [currentLang, selectedStyle]);

    /* ── Click-outside to close dropdown ── */
    useEffect(() => {
        if (!optionsOpen) return;
        const onDown = (e) => {
            if (optionsRef.current && !optionsRef.current.contains(e.target)) {
                setOptionsOpen(false);
            }
        };
        const onKey = (e) => {
            if (e.key === "Escape") setOptionsOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [optionsOpen]);

    const handleConvert = useCallback(() => {
        if (!jsonText.trim()) {
            setOutput("");
            return;
        }
        setOutput(generateCode(jsonText, selectedLang, selectedStyle, currentOptions));
    }, [jsonText, selectedLang, selectedStyle, currentOptions]);

    useEffect(() => {
        handleConvert();
    }, [handleConvert]);

    const toggleOption = (id) => {
        setOptionsByLang((prev) => ({
            ...prev,
            [selectedLang]: {
                ...prev[selectedLang],
                [id]: !prev[selectedLang]?.[id],
            },
        }));
    };

    const resetOptions = () => {
        setOptionsByLang((prev) => {
            const reset = {};
            for (const o of currentLang.options) reset[o.id] = o.default;
            return { ...prev, [selectedLang]: reset };
        });
    };

    const handleCopy = async () => {
        if (!output) return;
        await navigator.clipboard.writeText(output);
        setCopiedFlash(true);
        setTimeout(() => setCopiedFlash(false), 1400);
    };

    if (!activeItem) return <div className="ed-empty">No file selected</div>;

    const activeOptionCount = currentLang.options.filter(
        (o) => !!currentOptions[o.id]
    ).length;

    return (
        <div className="qt-root">
            <div className="qt-header">
                <div className="qt-lang-selector">
                    <label className="qt-lang-label">Language:</label>
                    <select
                        value={selectedLang}
                        onChange={(e) => setSelectedLang(e.target.value)}
                        className="qt-lang-dropdown"
                    >
                        {languages.map((lang) => (
                            <option key={lang.id} value={lang.id}>{lang.label}</option>
                        ))}
                    </select>

                    <label className="qt-lang-label">Style:</label>
                    <select
                        value={selectedStyle}
                        onChange={(e) => setSelectedStyle(e.target.value)}
                        className="qt-lang-dropdown"
                    >
                        {currentLang.styles.map((s) => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                    </select>

                    {currentLang.options.length > 0 && (
                        <div className="qt-options-wrap" ref={optionsRef}>
                            <button
                                className={`qt-options-trigger${optionsOpen ? " qt-options-trigger--open" : ""}`}
                                onClick={() => setOptionsOpen((v) => !v)}
                                title="Configure output options"
                                aria-haspopup="true"
                                aria-expanded={optionsOpen}
                            >
                                Options
                                {activeOptionCount > 0 && (
                                    <span className="qt-options-badge">{activeOptionCount}</span>
                                )}
                                <span className="qt-options-caret">▾</span>
                            </button>

                            {optionsOpen && (
                                <div className="qt-options-panel" role="menu">
                                    <div className="qt-options-panel-head">
                                        <span className="qt-options-panel-title">
                                            {currentLang.label} options
                                        </span>
                                        <button
                                            className="qt-options-reset"
                                            onClick={resetOptions}
                                            title="Reset to defaults"
                                        >
                                            Reset
                                        </button>
                                    </div>
                                    <div className="qt-options-panel-body">
                                        {currentLang.options.map((o) => (
                                            <label
                                                key={o.id}
                                                className="qt-option"
                                                title={o.hint || ""}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!currentOptions[o.id]}
                                                    onChange={() => toggleOption(o.id)}
                                                />
                                                <span className="qt-option-text">
                                                    <span className="qt-option-label">{o.label}</span>
                                                    {o.hint && (
                                                        <span className="qt-option-hint">{o.hint}</span>
                                                    )}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <button
                    className={`qt-copy-btn${copiedFlash ? " qt-copy-btn--active" : ""}`}
                    onClick={handleCopy}
                    title="Copy all"
                    disabled={!output}
                >
                    {copiedFlash ? "Copied!" : "Copy"}
                </button>
            </div>

            <div className="qt-body">
                {output ? (
                    <pre className="qt-code">{output}</pre>
                ) : (
                    <div className="qt-placeholder">No JSON to convert</div>
                )}
            </div>
        </div>
    );
}